import { Select, Upload } from "antd";
import { fromPairs, get, isEmpty } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlinePictureAsPdf, MdOutlineUploadFile } from "react-icons/md";
import Card from "shared/components/Card";
import Input from "shared/components/Input";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  DATE_TIME_FORMAT,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
} from "utils/constants";
import { PDF_ACCEPT } from "../pdf-upload";
import StaticUsageLine from "./StaticUsageLine";

const StaticDocumentFields = ({ form, document, onDownload }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const STATIC = t("STATIC", { returnObjects: true });
  const FORM = STATIC.FORM;
  const { values, set, file, pick } = form;
  const [usagesOpen, setUsagesOpen] = useState(false);
  const versions = get(document, "versions") || [];

  const { data: plansData } = useQuery(graphql.queries.INSURANCE_PLAN_CHOICES, {
    variables: { orderBy: [{ name: "ASC" }] },
  });
  const plans = useMemo(
    () =>
      (get(plansData, "insurancePlans.data") || []).map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [plansData],
  );
  const planNames = useMemo(
    () => fromPairs(plans.map(({ value, label }) => [value, label])),
    [plans],
  );

  return (
    <Card type="simple">
      <div className="static-documents__form">
        <StaticUsageLine
          usages={values.usages}
          plans={plans}
          planNames={planNames}
          open={usagesOpen}
          onOpenChange={setUsagesOpen}
          onChange={set("usages")}
        />
        <Input
          label={FORM.NAME}
          value={values.name}
          onChange={({ target: { value } }) => set("name")(value)}
        />
        <div className="static-documents__field">
          <span className="static-documents__label">{FORM.TYPE}</span>
          <Select
            className="static-documents__full"
            placeholder={FORM.TYPE_PLACEHOLDER}
            value={values.type ?? undefined}
            options={Object.keys(INSURANCE_PLAN_ATTACHMENTS_TYPES).map(
              (value) => ({ value, label: STATIC.TYPES[value] ?? value }),
            )}
            onChange={set("type")}
          />
        </div>
        <div className="static-documents__field">
          <span className="static-documents__label">{FORM.FILE}</span>
          {document && (
            <button
              type="button"
              className="static-documents__link"
              onClick={() => onDownload(document)}
            >
              {t("STATIC.FORM.CURRENT", {
                name: get(document, "attachment.name"),
              })}
            </button>
          )}
          <Upload.Dragger
            accept={PDF_ACCEPT}
            beforeUpload={pick}
            showUploadList={false}
            maxCount={1}
            className="dynamic-documents__drop"
          >
            {file ? (
              <div className="dynamic-documents__new-file">
                <MdOutlinePictureAsPdf size={20} />
                <span className="dynamic-documents__new-file-name">
                  {file.name}
                </span>
              </div>
            ) : (
              <>
                <MdOutlineUploadFile
                  size={32}
                  className="dynamic-documents__drop-icon"
                />
                <p className="dynamic-documents__drop-text">
                  {document ? FORM.REPLACE : FORM.DROP}
                </p>
              </>
            )}
          </Upload.Dragger>
        </div>
        {!isEmpty(versions) && (
          <div className="static-documents__field">
            <span className="static-documents__label">{FORM.VERSIONS}</span>
            {versions.map((version) => (
              <button
                key={version.attachmentId}
                type="button"
                className="static-documents__link"
                onClick={() => onDownload(document, version.attachmentId)}
              >
                {t("STATIC.FORM.VERSION_ITEM", {
                  name: version.name,
                  date: moment(version.savedAt).format(DATE_TIME_FORMAT),
                })}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default StaticDocumentFields;
